import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

export interface PotentialRegularSession {
	shouldPrompt: boolean;
	matchCount: number;
	earliestDate: string;

	surgeon_id: number;
	hospital_id: number;
	start_time: string;

	currentSessionId?: number;
	session_id?: number;

	matchedSessionIds: number[];
	matchedDates: string[];

	regularSessionDraft: RegularSessionDraft;

	reason?: string;
}

interface PotentialRegularSessionsState {
	items: PotentialRegularSession[];
	activeIndex: number;
	open: boolean;
}

const initialState: PotentialRegularSessionsState = {
	items: [],
	activeIndex: 0,
	open: false,
};

export interface RegularSessionDraft {
	surgeon_id: number;
	hospital_id: number;

	week_cycle: "A" | "B" | "C" | "D";
	week_day: string;

	am: boolean;
	pm: boolean;
	tw: boolean;

	start_time: string;
	end_time: string;

	start_date: string;
	end_date: string | null;
}

export const potentialRegularSessionsSlice = createSlice({
	name: "potentialRegularSessions",
	initialState,
	reducers: {
		setPotentialRegularSessions: (
			state,
			action: PayloadAction<PotentialRegularSession[]>,
		) => {
			state.items = action.payload;
			state.activeIndex = 0;
			state.open = action.payload.length > 0;
		},

		addPotentialRegularSessions: (
			state,
			action: PayloadAction<PotentialRegularSession[]>,
		) => {
			state.items = [...state.items, ...action.payload];
			state.open = state.items.length > 0;
		},

		clearPotentialRegularSessions: (state) => {
			state.items = [];
			state.activeIndex = 0;
			state.open = false;
		},

		openPotentialRegularSessionsPrompt: (state) => {
			state.open = state.items.length > 0;
		},

		closePotentialRegularSessionsPrompt: (state) => {
			state.open = false;
		},

		nextPotentialRegularSession: (state) => {
			if (state.items.length === 0) return;

			state.activeIndex =
				state.activeIndex >= state.items.length - 1
					? 0
					: state.activeIndex + 1;
		},

		previousPotentialRegularSession: (state) => {
			if (state.items.length === 0) return;

			state.activeIndex =
				state.activeIndex <= 0
					? state.items.length - 1
					: state.activeIndex - 1;
		},

		removePotentialRegularSessionAtIndex: (
			state,
			action: PayloadAction<number>,
		) => {
			state.items = state.items.filter(
				(_, index) => index !== action.payload,
			);

			if (state.items.length === 0) {
				state.activeIndex = 0;
				state.open = false;
				return;
			}

			if (state.activeIndex >= state.items.length) {
				state.activeIndex = state.items.length - 1;
			}
		},
	},
});

export const {
	setPotentialRegularSessions,
	addPotentialRegularSessions,
	clearPotentialRegularSessions,
	openPotentialRegularSessionsPrompt,
	closePotentialRegularSessionsPrompt,
	nextPotentialRegularSession,
	previousPotentialRegularSession,
	removePotentialRegularSessionAtIndex,
} = potentialRegularSessionsSlice.actions;

export const selectPotentialRegularSessions = (state: RootState) =>
	state.potentialRegularSessions.items;

export const selectPotentialRegularSessionsOpen = (state: RootState) =>
	state.potentialRegularSessions.open;

export const selectPotentialRegularSessionsActiveIndex = (state: RootState) =>
	state.potentialRegularSessions.activeIndex;

export const selectActivePotentialRegularSession = (state: RootState) => {
	const { items, activeIndex } = state.potentialRegularSessions;
	return items[activeIndex] ?? null;
};

export default potentialRegularSessionsSlice.reducer;
